import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../api/client';

interface ClaimRow {
  id: number;
  eventType: string;
  description: string | null;
  amountClaimed: number;
  status: 'PENDING' | 'PROCESSED' | 'DENIED';
  outcome: string | null;
  payoutAmount: number | null;
  filedAt: string;
  processedAt: string | null;
  planId: number | null;
  policyId: number | null;
  user: { id: number; email: string; firstName: string | null; lastName: string | null };
}

const STATUS_FILTERS = ['PENDING', 'PROCESSED', 'DENIED', 'ALL'] as const;

const statusColor: Record<string, string> = {
  PENDING: 'text-gold bg-gold/10',
  PROCESSED: 'text-success bg-success/10',
  DENIED: 'text-error bg-error/10',
};

export default function AdminCreditProtectClaims() {
  const [claims, setClaims] = useState<ClaimRow[]>([]);
  const [filter, setFilter] = useState<typeof STATUS_FILTERS[number]>('PENDING');
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<number | null>(null);
  const [outcome, setOutcome] = useState('');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutError, setPayoutError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback((status: typeof STATUS_FILTERS[number]) => {
    setLoading(true);
    api
      .get('/credit-protect/admin/claims', { params: status === 'ALL' ? {} : { status } })
      .then((res) => setClaims(res.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(filter);
  }, [filter, load]);

  const process = async (id: number, status: 'PROCESSED' | 'DENIED') => {
    if (status === 'PROCESSED') {
      const amount = Number(payoutAmount);
      if (!Number.isFinite(amount) || amount <= 0) {
        setPayoutError('Enter a positive payout amount to approve.');
        return;
      }
    }
    setPayoutError('');
    setSubmitting(true);
    try {
      await api.patch(`/credit-protect/admin/claims/${id}`, {
        status,
        outcome: outcome.trim() || undefined,
        payoutAmount: status === 'PROCESSED' ? Number(payoutAmount) : undefined,
      });
      setActingId(null);
      setOutcome('');
      setPayoutAmount('');
      load(filter);
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Could not process claim');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-textPrimary">Credit Protect Claims</h1>
        <div className="flex gap-2">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-sm px-3 py-1.5 rounded-lg border transition ${
                filter === s ? 'bg-gold/10 border-gold/40 text-gold' : 'bg-surface1 border-border text-textSecondary hover:border-gold/30'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-surface1 rounded-xl border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-textTertiary">
            <tr>
              <th className="text-left py-3 px-4">User</th>
              <th className="text-left py-3 px-4">Filed</th>
              <th className="text-left py-3 px-4">Event</th>
              <th className="text-left py-3 px-4">Amount</th>
              <th className="text-left py-3 px-4">Against</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-left py-3 px-4">Payout</th>
              <th className="text-left py-3 px-4">Outcome</th>
              <th className="text-left py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-textTertiary">Loading...</td></tr>
            ) : claims.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-textTertiary">No claims.</td></tr>
            ) : (
              claims.map((c) => (
                <React.Fragment key={c.id}>
                  <tr className="border-b border-border/60 hover:bg-surface2/50 transition">
                    <td className="py-3 px-4 text-textPrimary">
                      {[c.user.firstName, c.user.lastName].filter(Boolean).join(' ') || `User #${c.user.id}`}
                      <div className="text-textTertiary text-xs">{c.user.email}</div>
                    </td>
                    <td className="py-3 px-4 text-textTertiary">{new Date(c.filedAt).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-textSecondary">
                      {c.eventType.replace(/_/g, ' ')}
                      {c.description && <div className="text-textTertiary text-xs max-w-xs">{c.description}</div>}
                    </td>
                    <td className="py-3 px-4 text-textPrimary font-mono">${c.amountClaimed.toFixed(2)}</td>
                    <td className="py-3 px-4 text-textTertiary text-xs">
                      {c.policyId ? `Policy #${c.policyId}` : 'Base coverage'}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[c.status]}`}>{c.status}</span>
                    </td>
                    <td className="py-3 px-4 text-textPrimary font-mono">
                      {c.payoutAmount !== null ? `$${c.payoutAmount.toFixed(2)}` : '—'}
                    </td>
                    <td className="py-3 px-4 text-textTertiary max-w-xs">{c.outcome || '—'}</td>
                    <td className="py-3 px-4">
                      {c.status === 'PENDING' && (
                        <button
                          onClick={() => {
                            const opening = actingId !== c.id;
                            setActingId(opening ? c.id : null);
                            setPayoutAmount(opening ? String(c.amountClaimed) : '');
                            setPayoutError('');
                          }}
                          className="text-xs px-3 py-1.5 rounded-lg bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 transition"
                        >
                          Process
                        </button>
                      )}
                    </td>
                  </tr>
                  {actingId === c.id && (
                    <tr className="border-b border-border/60 bg-surface2/30">
                      <td colSpan={9} className="py-4 px-4">
                        <div className="flex gap-3 items-start">
                          <div className="w-40">
                            <label className="text-textTertiary text-xs">Payout amount (USD)</label>
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              max={c.amountClaimed}
                              value={payoutAmount}
                              onChange={(e) => setPayoutAmount(e.target.value)}
                              placeholder={`Up to $${c.amountClaimed.toFixed(2)}`}
                              className="w-full px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
                            />
                            <p className="text-textTertiary text-xs mt-1">Required to approve. Not needed to deny.</p>
                          </div>
                          <textarea
                            value={outcome}
                            onChange={(e) => setOutcome(e.target.value)}
                            placeholder="Outcome notes (optional)"
                            rows={2}
                            className="flex-1 px-3 py-2 bg-surface1 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold resize-none"
                          />
                          <button
                            onClick={() => process(c.id, 'PROCESSED')}
                            disabled={submitting}
                            className="text-sm px-4 py-2 rounded-lg bg-success/10 border border-success/30 text-success font-medium hover:bg-success/20 transition disabled:opacity-40"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => process(c.id, 'DENIED')}
                            disabled={submitting}
                            className="text-sm px-4 py-2 rounded-lg bg-error/10 border border-error/30 text-error font-medium hover:bg-error/20 transition disabled:opacity-40"
                          >
                            Deny
                          </button>
                        </div>
                        {payoutError && (
                          <p className="text-error text-xs mt-2">{payoutError}</p>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
